// src/modules/app-views/fiscal-config/pages/Document/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { SimpleFormProvider } from 'components/SimpleComponents/SimpleFormProvider';
import useZustand from 'hooks/useZustand';
import { Tabs } from 'antd';
import translateX from 'utils/translateX';
import { useEffect, useMemo, useState } from 'react';
import { useDocumentContext } from 'components/core/DocumentProvider';
import appService from 'services/appService';
import SimpleFormProviderInitializer from 'components/SimpleComponents/SimpleFormProviderInitializer';
import SimpleLoadingArea from 'components/SimpleComponents/SimpleLoadingArea';
import { buildInitialValues } from 'utils/mock';
import useFormInstance from 'hooks/useFormInstance';
import CardHeaderWithCountry from 'layouts/Pages/PageHeader/CardHeaderWithCountry';
import authService from 'services/authService';
import fiscalService from 'services/fiscalService';
import useFiscalDataTracking from 'hooks/useFiscalDataTracking';
import CertificateTab from 'modules/app-views/fiscalConfig/pages/Document/tabs/CertificateTab';
import NfeTab from 'modules/app-views/fiscalConfig/pages/Document/tabs/NfeTab';
import NfceTab from 'modules/app-views/fiscalConfig/pages/Document/tabs/NfceTab';
import NfseTab from 'modules/app-views/fiscalConfig/pages/Document/tabs/NfseTab';
import OperacaoFiscalTab from 'modules/app-views/fiscalConfig/pages/Document/tabs/OperacaoFiscalTab';
import { FiscalStateProvider } from 'modules/app-views/fiscalConfig/pages/Document/context/FiscalStateContext';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const isMockStep = false;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const CompaniesDocument = props => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const storedSubscription = useZustand('auth', 'subscription');
  const { tab, initTab, handleTabChange, availableTabs, buildTabs, data, viewMode } =
    useDocumentContext();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  // Get upsert from global state - auto-fetch is handled by DocumentProvider
  const { upsert, urlInfo } = useZustand('global', 'useApi');
  const { formRef, form, isInitializedRef, excludeFieldsFromReset } = useFormInstance({ data });
  const _id = urlInfo?.params?._id;

  // Centralized fiscal data tracking hook
  const { getSavedFiscalData, updateSavedFiscalData } = useFiscalDataTracking(data);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const locale = appService.getLocale();
  const requiredFields = [
    'countryCode',

    'cnpj.value',
    'name',
    'fullName',
    'phone.value',
    'email.value',
    'foundAt',

    'address.zip',
    'address.number',
    'address.countryCode',
  ];

  if (locale !== 'BR') {
    requiredFields.push('taxId', 'address.address1', 'address.region', 'address.city');
  }

  const tabsItems = useMemo(() => {
    const src = Array.isArray(availableTabs)
      ? availableTabs
      : availableTabs
        ? Object.values(availableTabs)
        : [];
    return src;
  }, [availableTabs]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  // NOTE: useMemo instead of useState to react when data becomes available
  const initialValues = useMemo(() => buildInitialValues(isMockStep, data), [data]);
  const [disabledFields, setDisabledFields] = useState([]);
  const [state, setState] = useState({
    dataOrigin: 'new_data',
    entityType: 'company',
    entityProfile: ['subscription', 'company'],
  });

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // Check if certificate exists to show/hide fiscal tabs
  const hasCertificate = !!data?.fiscal?.certificate;

  // Subscription resources determine which fiscal tabs are visible
  const subscriptionResources = storedSubscription?.resources || [];

  useEffect(() => {
    const childProps = {
      ...props,
      initTab,
      state,
      setState,
      viewMode,
      hasCertificate,
      subscriptionResources,
    };
    const itemsLocal = buildTabsArray(childProps);
    buildTabs(itemsLocal);
  }, [state, initTab, viewMode, hasCertificate, subscriptionResources]); // keep behavior

  // NOTE: savedFiscalDataRef initialization is now handled by useFiscalDataTracking hook

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleFinish = async ({ values, allValues }) => {
    try {
      // Get _id from multiple sources (fallback pattern like SubscriptionsDocument)
      const entityId = values?._id || allValues?._id || data?._id;

      // NOTE: Fiscal service validation is now done in each tab's toggle handler (NfceTab, NfeTab, NfseTab)
      // The validateFiscalServicesBeforeSave call was removed to avoid duplicate validation

      // Ensure fiscalData is included in the save operation
      // fiscalData fields come from the FiscalTab form inputs
      // Normalize fiscalData to convert cleared fields (allowClear) to explicit null
      const normalizedFiscalData = allValues?.fiscalData
        ? fiscalService.normalizeFiscalData(allValues.fiscalData, getSavedFiscalData())
        : undefined;

      const dataToSave = {
        ...values,
        ...(normalizedFiscalData && { fiscalData: normalizedFiscalData }),
      };

      // Show "saving" message only AFTER fiscal validations pass (if any)
      // This provides better UX: user sees "Validating..." then "Saving..."
      await appService.message({ flag: 'i', text: 'saving_data', key: 'saveContext' });

      await upsert({ dataSet: dataToSave, _id: entityId });

      // Handle fiscal field clearing - capture BEFORE signInRefresh to prevent flash
      if (allValues?.fiscalData) {
        const originalFiscalData = getSavedFiscalData();
        const clearedFields = fiscalService.getClearedFiscalFields(
          allValues.fiscalData,
          originalFiscalData
        );

        if (clearedFields.length > 0) {
          // Mark these fields to be excluded from the next auto-reset in useFormInstance
          excludeFieldsFromReset(clearedFields);
        }

        // Update the tracking ref
        updateSavedFiscalData(allValues.fiscalData);
      }

      // refresh account subscription data if user is editing an main company linked with a subscription
      if (storedSubscription?.entityId === entityId) await authService.signInRefresh();

      // Clear the form fields after signInRefresh (the excluded fields won't flash back)
      if (allValues?.fiscalData) {
        fiscalService.clearFiscalFieldsInForm(form, allValues.fiscalData, getSavedFiscalData());
      }
    } catch (err) {
      console.error('[Companies.handleFinish] error =>', err);
      throw err;
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <FiscalStateProvider data={data}>
      <div className="container mt-3">
        <SimpleFormProvider
          formRef={formRef}
          onFinish={handleFinish}
          showSubmitButton={false}
          initFocus={locale === 'BR' ? 'cnpj.value' : 'name'}
          additionalValues={{
            entityProfile: 'company',
          }}
          reloadEntirePage={true}
          ignoreResetFormOnSubmit={true}
          requiredFields={requiredFields}
          disabledFields={disabledFields}
          locked={viewMode === 'view'}
          showInfoMessage={false}
          // forceViewAfterSave={false}
          initialValues={initialValues}
          data={data}
        >
          <SimpleFormProviderInitializer data={data} isInitializedRef={isInitializedRef} />

          <div style={{ position: 'relative' }}>
            <CardHeaderWithCountry
              overlapsBackground
              data={data}
              avatarName={data?.organization}
              watchField="countryCode"
            />

            <SimpleLoadingArea
              name="cardHeader"
              offsetTop={0}
              showProgress={false}
              zIndex={100}
              borderRadius={8}
            />
          </div>

          <Tabs
            type="line"
            tabPlacement="top"
            activeKey={tab}
            rootClassName="tab-in-form no-content-padding"
            items={tabsItems}
            onChange={handleTabChange}
            animated={{ inkBar: true, tabPane: false }}
            destroyOnHidden={false}
          />
        </SimpleFormProvider>
      </div>
    </FiscalStateProvider>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default CompaniesDocument;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function buildTabsArray(props) {
  const { initTab, viewMode, hasCertificate, subscriptionResources = [] } = props;

  // Check if subscription has the required resource for each fiscal type
  // NFe and NFCe share the same resources (moduleresources_nfe, moduleresources_nfce)
  const hasNfe = subscriptionResources.includes('moduleresources_nfe');
  const hasNfce = subscriptionResources.includes('moduleresources_nfce');
  const hasNfse = subscriptionResources.includes('moduleresources_nfse');

  return [
    {
      key: initTab,
      label: <span>{translateX('fiscal_data')}</span>,
      hide: false,
      disabled: false,
      children: <CertificateTab {...props} tabName={initTab} />,
    },
    {
      key: 'nfe',
      label: <span>{translateX('nfe')}</span>,
      hide: !hasCertificate || !hasNfe,
      disabled: false,
      children: <NfeTab {...props} tabName="nfe" />,
    },
    {
      key: 'nfce',
      label: <span>{translateX('nfce')}</span>,
      hide: !hasCertificate || !hasNfce,
      disabled: false,
      children: <NfceTab {...props} tabName="nfce" />,
    },
    {
      key: 'nfse',
      label: <span>{translateX('nfse')}</span>,
      hide: !hasCertificate || !hasNfse,
      disabled: false,
      children: <NfseTab {...props} tabName="nfse" />,
    },
    // {
    //   key: 'fiscalOerations',
    //   label: <span>{translateX('fiscal_operations')}</span>,
    //   hide: !hasCertificate,
    //   disabled: false,
    //   children: <OperacaoFiscalTab {...props} tabName="operacao_fiscal" />,
    // },
  ];
}
